import { del, get, post, put } from '@api/api-client';
import {
  appendPaginationQuery,
  type PaginatedResponse,
  type PaginationParams,
} from '@api/pagination';

interface CategoryDetailCountryResponse {
  id: string;
  name: string;
  code: string;
}

interface CategoryDetailResponse {
  id: string;
  countryId: string;
  name: string;
  marketplaceId1: number;
  marketplaceId2: number | null;
  marketplaceId3: number | null;
  marketplaceId4: number | null;
  marketplaceId5: number | null;
  marketplaceId6: number | null;
  marketplaceId7: number | null;
  marketplaceId8: number | null;
  marketplaceId9: number | null;
  marketplaceId10: number | null;
  country: CategoryDetailCountryResponse;
}

export interface CategoryResponse {
  id: string;
  externalId: number | null;
  name: string;
  isClinic: boolean;
  isGlobal: boolean;
  createdAt: string;
  updatedAt: string;
  details: CategoryDetailResponse[];
}

export interface CreateCategoryDetailDTO {
  countryId: string;
  name: string;
  marketplaceId1?: number;
  marketplaceId2?: number;
  marketplaceId3?: number;
  marketplaceId4?: number;
  marketplaceId5?: number;
  marketplaceId6?: number;
  marketplaceId7?: number;
  marketplaceId8?: number;
  marketplaceId9?: number;
  marketplaceId10?: number;
}

export interface CreateCategoryDTO {
  name: string;
  externalId?: number;
  isClinic?: boolean;
  isGlobal?: boolean;
  details: CreateCategoryDetailDTO[];
}

export interface UpdateCategoryDTO {
  name?: string;
  externalId?: number | null;
  isClinic?: boolean;
  isGlobal?: boolean;
  details?: CreateCategoryDetailDTO[];
}

export type CategorySortField = 'name' | 'externalId' | 'detailName';
export type SortDirection = 'asc' | 'desc';

export interface GetCategoriesParams extends PaginationParams {
  search?: string;
  sortBy?: CategorySortField;
  sortDir?: SortDirection;
  isClinic?: boolean;
  isGlobal?: boolean;
  countryId?: string;
}

/** Fetches categories with pagination, search, sorting, and filtering. */
export function getCategories(
  params?: GetCategoriesParams,
): Promise<PaginatedResponse<CategoryResponse>> {
  let path = appendPaginationQuery('/categories', params);
  if (params?.search) {
    const sep = path.includes('?') ? '&' : '?';
    path = `${path}${sep}search=${encodeURIComponent(params.search)}`;
  }
  if (params?.sortBy) {
    const sep = path.includes('?') ? '&' : '?';
    path = `${path}${sep}sortBy=${params.sortBy}`;
  }
  if (params?.sortDir) {
    const sep = path.includes('?') ? '&' : '?';
    path = `${path}${sep}sortDir=${params.sortDir}`;
  }
  if (params?.isClinic !== undefined) {
    const sep = path.includes('?') ? '&' : '?';
    path = `${path}${sep}isClinic=${params.isClinic}`;
  }
  if (params?.isGlobal !== undefined) {
    const sep = path.includes('?') ? '&' : '?';
    path = `${path}${sep}isGlobal=${params.isGlobal}`;
  }
  if (params?.countryId) {
    const sep = path.includes('?') ? '&' : '?';
    path = `${path}${sep}countryId=${encodeURIComponent(params.countryId)}`;
  }
  return get<PaginatedResponse<CategoryResponse>>(path);
}

/** Creates a new category. */
export function createCategory(data: CreateCategoryDTO): Promise<CategoryResponse> {
  return post<CategoryResponse>('/categories', data);
}

/** Updates an existing category by ID. */
export function updateCategory(id: string, data: UpdateCategoryDTO): Promise<CategoryResponse> {
  return put<CategoryResponse>(`/categories/${id}`, data);
}

interface ImportCategoryDetail {
  locale: string;
  name: string;
  marketplaceId1: number;
  marketplaceId2?: number;
  marketplaceId3?: number;
  marketplaceId4?: number;
  marketplaceId5?: number;
  marketplaceId6?: number;
  marketplaceId7?: number;
  marketplaceId8?: number;
  marketplaceId9?: number;
  marketplaceId10?: number;
}

export interface ImportCategoryRow {
  externalId: number;
  baseName: string;
  isClinic: boolean;
  /** Omitted when the file has no `is_global` column, which leaves the flag as is. */
  isGlobal?: boolean;
  details: ImportCategoryDetail[];
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

/** Deletes a category by ID. */
export function deleteCategory(id: string): Promise<void> {
  return del(`/categories/${id}`);
}

/** Imports categories from parsed CSV data. */
export function importCategories(categories: ImportCategoryRow[]): Promise<ImportResult> {
  return post<ImportResult>('/categories/import', { categories });
}
